import { AxiosError } from 'axios'

import I18n from '../i18n'

export type ApiError<T = any> = AxiosError<
  {
    message?: string
    errorMessage?: string
    messageParameter?: Record<string, string>
    /** errorMessage の補足として表示する失敗原因（サイトコントローラーが返したエラーの内容など） */
    causeMessage?: string | null
    error_code?: string
    errors?: { [propName: string]: string[] }
  } & T
>

export const isApiError = (arg: any): arg is ApiError => {
  return (
    arg?.response?.data?.message !== undefined ||
    arg?.response?.data?.errorMessage !== undefined ||
    arg?.response?.data?.error_code !== undefined ||
    arg?.response?.data?.errors !== undefined
  )
}

const HOTEL_PMS_ERROR_CODE_MAP: Record<string, string> = {
  hotel_pms_context_missing: 'HotelPms.Error.contextMissing',
  hotel_pms_screen_denied: 'HotelPms.Error.screenDenied',
  hotel_pms_read_only: 'HotelPms.Error.readOnly',
  hotel_pms_pii_forbidden: 'HotelPms.Error.piiForbidden',
  hotel_pms_export_denied: 'HotelPms.Error.exportDenied',
  hotel_pms_operation_denied: 'HotelPms.Error.operationDenied',
}

const resolveHotelPmsErrorMessage = (errorCode?: string): string | undefined => {
  if (!errorCode) return undefined
  const key = HOTEL_PMS_ERROR_CODE_MAP[errorCode]
  if (!key) return undefined
  return I18n.t(key)
}

/** スタッフ確認でキャンセルしたとき、またはセッション再認証後に元リクエストを破棄したとき（通知・親モーダル制御の判別に使う） */
export const isStaffGuardCancelledError = (error: unknown): boolean =>
  Boolean(
    error &&
    typeof error === 'object' &&
    ((error as { isStaffGuardCancelled?: boolean }).isStaffGuardCancelled ||
      (error as { isStaffGuardSessionReauthAbort?: boolean }).isStaffGuardSessionReauthAbort ||
      (error as { isHotelPmsReadOnlyGuardCancelled?: boolean }).isHotelPmsReadOnlyGuardCancelled),
  )

/** スタッフ再認証後、元リクエストを意図的に中断した制御エラーか */
export const isStaffGuardSessionReauthAbortError = (error: unknown): boolean =>
  Boolean(error && typeof error === 'object' && (error as { isStaffGuardSessionReauthAbort?: boolean }).isStaffGuardSessionReauthAbort)

/**
 * `staff-mutation-guard-store` が投げる制御フロー用の Error 一式。
 * いずれも「バグ」ではなく axios ガードの結果なので Sentry には送らない。
 */
export const isStaffMutationGuardControlError = (error: unknown): boolean =>
  Boolean(
    error &&
    typeof error === 'object' &&
    ((error as { isStaffGuardCancelled?: boolean }).isStaffGuardCancelled ||
      (error as { isStaffGuardSessionReauthAbort?: boolean }).isStaffGuardSessionReauthAbort ||
      (error as { isStaffGuardModalNotReady?: boolean }).isStaffGuardModalNotReady ||
      (error as { isHotelPmsReadOnlyGuardCancelled?: boolean }).isHotelPmsReadOnlyGuardCancelled),
  )

/**
 * ログ送信や画面表示に使う文字列をエラーから取り出す。
 *
 * ePOS SDK などコールバックで文字列を reject するものがあり、`error.message` だけでは undefined になる。
 * undefined のキーは送信 JSON から欠落し、ログにエラー内容が残らないため、必ず文字列を返す。
 */
export const toErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error
  }
  const message = (error as { message?: unknown })?.message
  if (typeof message === 'string') {
    return message
  }
  if (error === null || error === undefined) {
    return 'unknown error'
  }
  try {
    // SDK が渡すステータスオブジェクトなど、String() では [object Object] になるものも内容を残す
    return JSON.stringify(error) || String(error)
  } catch {
    return String(error)
  }
}

export const errorHandler = (error: AxiosError | any, options?: { mustShow: boolean }) => {
  /** モーダルでキャンセルした場合は意図的な中断のため通知しない */
  if (isStaffGuardCancelledError(error)) {
    return
  }

  if (!isApiError(error)) return alert(error)
  if (error?.response?.data?.mustShow && !options?.mustShow) return

  const errorResponseData = error?.response?.data
  if (!errorResponseData) return alert(error)

  const hotelPmsErrorText = resolveHotelPmsErrorMessage(errorResponseData?.error_code)
  const mainErrorText = [
    I18n.t(errorResponseData?.message || errorResponseData?.errorMessage || '', errorResponseData?.messageParameter),
    errorResponseData?.causeMessage,
  ]
    .filter(Boolean)
    .join('\n')

  const errorsArray: ({ message: string; params?: string } | string)[][] = Object.values(errorResponseData?.errors || [])

  let errorText = ''

  for (const [indexTop, topErrors] of errorsArray.entries() as any) {
    if (indexTop === 0 && errorText) errorText += '\n'
    if (Array.isArray(topErrors)) {
      for (const [index, error] of topErrors.entries() as any) {
        if (typeof error === 'object') {
          errorText += (indexTop !== 0 || index !== 0 ? '\n' : '') + I18n.t(error.message, error.params)
        } else if (typeof error === 'string') {
          errorText += (indexTop !== 0 || index !== 0 ? '\n' : '') + I18n.t(error)
        }
      }
    }
  }

  if (errorText === '\n') errorText = ''
  if (!errorText && !mainErrorText && !hotelPmsErrorText) errorText = String(error)

  alert(errorText || hotelPmsErrorText || mainErrorText)
}
